import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Sparkles, Copy, Check, Tag, List, Hash, FileText } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { id: "amazon", label: "Amazon" },
    { id: "shopify", label: "Shopify" },
    { id: "etsy", label: "Etsy" },
    { id: "ebay", label: "eBay" },
];

function CopyBtn({ text, id }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };
    return (
        <button onClick={copy} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 bg-surface-2 text-xs text-zinc-400 hover:text-accent hover:border-accent/40 transition-colors" data-testid={`showcase-copy-${id}`}>
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function ExampleCard({ ex, expanded, onToggle }) {
    const [tab, setTab] = useState(ex.marketplace || "amazon");
    const r = ex.result || {};
    const mp = r.marketplaces?.[tab] || {};

    return (
        <div className="surface overflow-hidden" data-testid={`showcase-card-${ex.id}`}>
            <div className="grid md:grid-cols-12 gap-0">
                {/* Image */}
                <div className="md:col-span-5 bg-surface-2 relative overflow-hidden">
                    <img src={ex.image_url} alt={ex.title} className="w-full h-full object-cover min-h-[280px] md:min-h-[420px]" />
                    <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-bg/80 backdrop-blur border border-white/10">
                        <span className="overline !text-zinc-300 !text-[10px]">{ex.category}</span>
                    </div>
                </div>

                {/* Preview content */}
                <div className="md:col-span-7 p-6 md:p-8 flex flex-col">
                    <div className="overline mb-3 flex items-center gap-2">
                        <Tag className="w-3 h-3" /> SEO Title
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-zinc-100 mb-4">
                        {r.seo_title}
                    </h3>

                    <p className="text-zinc-400 leading-relaxed mb-5">{r.short_description}</p>

                    <div className="mb-5">
                        <div className="overline mb-3 flex items-center gap-2">
                            <List className="w-3 h-3" /> Top bullets
                        </div>
                        <ul className="space-y-1.5">
                        {(r.bullet_points || []).slice(0, 3).map((b, i) => (
                            <li key={`sc-bullet-${i}-${b.slice(0, 30)}`} className="flex gap-2.5 text-sm text-zinc-300">
                                    <span className="text-accent mt-0.5 flex-shrink-0">▸</span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button
                        onClick={onToggle}
                        className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline self-start"
                        data-testid={`showcase-expand-${ex.id}`}
                    >
                        {expanded ? "Hide full output" : "See full output"} <ArrowRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
                    </button>
                </div>
            </div>

            {/* Expanded details */}
            {expanded && (
                <div className="border-t border-white/5 p-6 md:p-8 space-y-6 animate-fade-in">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="overline flex items-center gap-2"><FileText className="w-3 h-3" /> Long description</span>
                                <CopyBtn text={r.long_description} id={`${ex.id}-long`} />
                            </div>
                            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{r.long_description}</p>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="overline flex items-center gap-2"><Hash className="w-3 h-3" /> Meta tags</span>
                                    <CopyBtn text={`${r.meta_title}\n${r.meta_description}`} id={`${ex.id}-meta`} />
                                </div>
                                <div className="text-zinc-200 mb-1">{r.meta_title}</div>
                                <div className="text-sm text-zinc-500">{r.meta_description}</div>
                            </div>
                            {(r.keywords || []).length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="overline">SEO keywords</span>
                                        <CopyBtn text={(r.keywords || []).join(", ")} id={`${ex.id}-kw`} />
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {r.keywords.map((k, i) => (
                                            <span key={`sc-kw-${i}-${k}`} className="px-2 py-0.5 rounded-md bg-surface-2 border border-white/10 text-xs font-mono text-zinc-300">{k}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {(r.variant_descriptions || []).length > 0 && (
                        <div>
                            <div className="overline mb-3">Variant descriptions</div>
                            <div className="grid md:grid-cols-2 gap-3">
                                {r.variant_descriptions.map((v, i) => (
                                    <div key={`sc-var-${i}-${v.variant}`} className="rounded-md border border-white/10 bg-surface-2 p-4">
                                        <div className="font-medium text-zinc-100 mb-1">{v.variant}</div>
                                        <p className="text-sm text-zinc-400">{v.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Marketplace tabs */}
                    <div>
                        <div className="overline mb-3">Marketplace formats</div>
                        <div className="flex gap-1 border-b border-white/5">
                            {TABS.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setTab(t.id)}
                                    className={`px-4 py-2 text-xs font-medium relative transition-colors ${tab === t.id ? "text-accent" : "text-zinc-500 hover:text-zinc-200"}`}
                                    data-testid={`showcase-tab-${ex.id}-${t.id}`}
                                >
                                    {t.label}
                                    {tab === t.id && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-accent" />}
                                </button>
                            ))}
                        </div>
                        <div className="bg-surface-2 border border-white/10 border-t-0 rounded-b-md p-5">
                            {Object.keys(mp).length === 0 ? (
                                <div className="text-zinc-500 font-mono text-xs">No copy for {tab}.</div>
                            ) : (
                                <div className="space-y-4 text-sm">
                                    {mp.title && (
                                        <div>
                                            <div className="overline mb-1">Title</div>
                                            <p className="text-zinc-100">{mp.title}</p>
                                        </div>
                                    )}
                                    {mp.bullets && (
                                        <div>
                                            <div className="overline mb-1">Bullets</div>
                                            <ul className="space-y-1">
                                                {mp.bullets.map((b, i) => <li key={`sc-mpb-${i}-${b.slice(0, 30)}`} className="text-zinc-300"><span className="text-accent mr-2">▸</span>{b}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {mp.description && (
                                        <div>
                                            <div className="overline mb-1">Description</div>
                                            <p className="text-zinc-300 whitespace-pre-wrap">{mp.description}</p>
                                        </div>
                                    )}
                                    {mp.tags && (
                                        <div>
                                            <div className="overline mb-1">Tags</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {mp.tags.map((t, i) => <span key={`sc-mptag-${i}-${t}`} className="px-2 py-0.5 rounded-md bg-accent/5 border border-accent/20 text-xs font-mono text-accent">{t}</span>)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Showcase() {
    const [examples, setExamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        api.get("/showcase")
            .then(({ data }) => setExamples(data))
            .catch(() => setExamples([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="relative" data-testid="showcase-page">
            <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-accent/5 blur-[120px]" />
            </div>

            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-14">
                    <div className="overline mb-3">Real outputs</div>
                    <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-4">
                        See what listcraft writes<span className="text-accent">.</span>
                    </h1>
                    <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
                        Below are actual product photos paired with the listings our AI generates from them — SEO titles, persuasive copy, bullets, variants and marketplace-specific formats.
                    </p>
                </div>

                {loading ? (
                    <div className="surface p-16 text-center font-mono text-sm text-zinc-500">Loading examples...</div>
                ) : (
                    <div className="space-y-6">
                        {examples.map((ex) => (
                            <ExampleCard
                                key={ex.id}
                                ex={ex}
                                expanded={expandedId === ex.id}
                                onToggle={() => setExpandedId(expandedId === ex.id ? null : ex.id)}
                            />
                        ))}
                    </div>
                )}

                {/* CTA at bottom */}
                <div className="mt-16 surface p-10 md:p-14 text-center">
                    <Sparkles className="w-6 h-6 text-accent mx-auto mb-4" />
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-3">
                        Ready to write yours?
                    </h2>
                    <p className="text-zinc-500 mb-8 max-w-md mx-auto">
                        Sign up and you&apos;ll get one free listing — no card required. Generate from your own product photo in under a minute.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/register" className="btn-primary" data-testid="showcase-cta-register">
                            Try it free <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/pricing" className="btn-ghost" data-testid="showcase-cta-pricing">
                            See pricing
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
